import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  PROPERTY_TYPES,
  METER_TYPES,
  METER_UNIT_SUGGESTION,
  INSURANCE_TYPES,
  SALUTATIONS,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, ArrowRight, Buildings, MapTrifold, ListBullets, Trash, UserPlus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PropertyMap from "@/components/PropertyMap";
import SearchableCombobox from "@/components/SearchableCombobox";

export default function Properties() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [wegCommunities, setWegCommunities] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  // Sprint 5b — Sortierung
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("properties.sortMode") || "manual");
  const [manualOrder, setManualOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("properties.manualOrder") || "[]"); } catch { return []; }
  });
  const [dragId, setDragId] = useState(null);

  const loadOwners = async () => {
    const { data } = await api.get("/contacts", { params: { role: "owner" } });
    setOwners(data);
  };

  const loadWegCommunities = async () => {
    try {
      const { data } = await api.get("/contacts", { params: { contact_type: "weg_community" } });
      setWegCommunities(data);
    } catch { setWegCommunities([]); }
  };

  const load = async () => {
    const { data } = await api.get("/properties");
    setItems(data);
    await loadOwners();
    await loadWegCommunities();
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem("properties.sortMode", sortMode); }, [sortMode]);
  useEffect(() => { localStorage.setItem("properties.manualOrder", JSON.stringify(manualOrder)); }, [manualOrder]);

  const sortedItems = useMemo(() => {
    const arr = [...items];
    if (sortMode === "number") return arr.sort((a, b) => (a.property_number || "").localeCompare(b.property_number || "", "de"));
    if (sortMode === "name") return arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
    if (sortMode === "units") return arr.sort((a, b) => (b.units_count || 0) - (a.units_count || 0));
    // manual: use manualOrder; append new properties at the end
    const idx = (id) => { const i = manualOrder.indexOf(id); return i < 0 ? 9999 : i; };
    return arr.sort((a, b) => {
      const di = idx(a.id) - idx(b.id);
      if (di !== 0) return di;
      return (a.property_number || "").localeCompare(b.property_number || "", "de");
    });
  }, [items, sortMode, manualOrder]);

  const handleDragStart = (id) => setDragId(id);
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const order = manualOrder.length ? [...manualOrder] : items.map((p) => p.id);
    const ids = order.filter((id) => id !== dragId);
    const ti = ids.indexOf(targetId);
    ids.splice(ti < 0 ? ids.length : ti, 0, dragId);
    // ensure all items have a place
    items.forEach((p) => { if (!ids.includes(p.id)) ids.push(p.id); });
    setManualOrder(ids);
    setDragId(null);
    setSortMode("manual");
    toast.success("Reihenfolge gespeichert");
  };

  return (
    <div className="space-y-4" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Objekte</h1>
          <p className="text-sm text-slate-500 mt-1">Liegenschaften, Gebäude und Einheiten</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-property-btn">
              <Plus size={16} /> Neues Objekt
            </Button>
          </DialogTrigger>
          <PropertyDialog
            key={open ? "open" : "closed"}
            owners={owners}
            reloadOwners={loadOwners}
            wegCommunities={wegCommunities}
            reloadWegCommunities={loadWegCommunities}
            onSaved={(created) => {
              setOpen(false);
              load();
              // 2026-09 · Direkt in das neu angelegte Objekt springen — Einheiten-Tab
              if (created?.id) {
                setTimeout(() => navigate(`/objekte/${created.id}?tab=units`), 150);
              }
            }}
          />
        </Dialog>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-sm border border-slate-200 bg-white p-1" data-testid="view-toggle">
          <button onClick={() => setView("list")}
            className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1.5 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            data-testid="view-list">
            <ListBullets size={14} /> Liste
          </button>
          <button onClick={() => setView("map")}
            className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1.5 ${view === "map" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            data-testid="view-map">
            <MapTrifold size={14} /> Karte
          </button>
        </div>
        {view === "list" && (
          <div className="inline-flex items-center gap-2 text-xs">
            <span className="text-slate-500">Sortierung:</span>
            <select
              value={sortMode} onChange={(e) => setSortMode(e.target.value)}
              className="h-8 border border-slate-200 rounded-sm text-xs bg-white px-2"
              data-testid="properties-sort-mode"
            >
              <option value="manual">Manuell (Drag &amp; Drop)</option>
              <option value="number">Objektnummer ↑</option>
              <option value="name">Name (A–Z)</option>
              <option value="units">Einheiten (meiste zuerst)</option>
            </select>
            {sortMode === "manual" && (
              <span className="text-[10px] text-slate-400 italic">Karten ziehen, um zu sortieren</span>
            )}
          </div>
        )}
      </div>

      {view === "map" ? <PropertyMap /> : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-500">
            <Buildings size={32} className="mx-auto mb-2 text-slate-300" />
            Noch keine Objekte angelegt.
          </div>
        )}
        {sortedItems.map((p) => (
          <Link to={`/objekte/${p.id}`} key={p.id}
            draggable={sortMode === "manual"}
            onDragStart={(e) => { if (sortMode === "manual") { handleDragStart(p.id); e.dataTransfer.effectAllowed = "move"; } }}
            onDragOver={sortMode === "manual" ? handleDragOver : undefined}
            onDrop={(e) => { if (sortMode === "manual") { e.preventDefault(); handleDrop(p.id); } }}
            className={`bg-white border rounded-md p-4 hover:border-slate-400 hover:shadow-md transition-all group shadow-sm
              ${sortMode === "manual" ? "cursor-grab active:cursor-grabbing border-stone-200" : "border-stone-200"}
              ${dragId === p.id ? "opacity-40" : ""}`}
            data-testid={`property-card-${p.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold font-mono">{p.property_number}</div>
                <h3 className="font-semibold text-slate-900 mt-0.5">{p.name}</h3>
              </div>
              <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-900 mt-1" />
            </div>
            <div className="text-sm text-slate-600">
              {p.address?.street} {p.address?.house_number}, {p.address?.zip} {p.address?.city}
            </div>
            <div className="text-xs text-slate-500 mt-1">{PROPERTY_TYPES[p.property_type] || p.property_type}</div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-100">
              <Stat label="Einheiten" value={p.units_count} />
              <Stat label="Vermietet" value={p.active_tenancies} color="text-emerald-700" />
              <Stat label="Leer" value={p.vacant_units} color={p.vacant_units > 0 ? "text-amber-700" : "text-slate-700"} />
            </div>
          </Link>
        ))}
      </div>
      )}
    </div>
  );
}

function Stat({ label, value, color = "text-slate-900" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${color}`}>{value ?? 0}</div>
    </div>
  );
}

// ============================================================
// Property creation dialog
// ============================================================

function newMeter() {
  return { meter_number: "", meter_type: "heat_quantity", location: "", measurement_unit: "kWh" };
}
function newInsurance() {
  return { company: "", policy_number: "", insurance_type: "building", contract_start: "", contract_end: "", notes: "" };
}

function PropertyDialog({ owners, reloadOwners, wegCommunities, reloadWegCommunities, onSaved }) {
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [wegDialogOpen, setWegDialogOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({
    name: "", property_type: "multi_family",
    // 2026-02 (iter120) — Verwaltungsart bereits bei Anlage konfigurierbar
    object_type: "rental",
    weg_management: "",
    weg_total_shares: 1000,
    address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
    // 2026-07 — Zusätzliche Anschrift (z. B. Vorderhaus/Hinterhaus, Eckhäuser)
    has_secondary_address: false,
    secondary_address_label: "",
    secondary_address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
    owner_contact_id: "",
    // 2026-02 · Verwaltungsbeginn (optional später als „Eigentum seit")
    management_start: "",
    caretaker_user_id: "",
    // building
    build_year: "", renovation_year: "",
    land_area: "", living_area: "", commercial_area: "",
    unit_count: "", floor_count: "",
    has_elevator: false, has_basement: false, has_attic: false,
    // heating & energy
    heating_type: "", heating_energy: "", hot_water_type: "",
    energy_certificate_type: "", energy_value: "", energy_class: "", energy_cert_valid_until: "",
    // lists
    meters: [],
    insurances: [],
    notes: "",
  });

  useEffect(() => {
    api.get("/users/staff-list").then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setAddr = (k, v) => setForm((s) => ({ ...s, address: { ...s.address, [k]: v } }));
  const setSecAddr = (k, v) => setForm((s) => ({ ...s, secondary_address: { ...s.secondary_address, [k]: v } }));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const str = (v) => (v === "" ? null : v);

  // PLZ-Auto-Lookup: bei 5-stelliger PLZ Stadt + KFZ vorschlagen
  const [plzHit, setPlzHit] = useState(null);
  useEffect(() => {
    const z = (form.address?.zip || "").replace(/\D/g, "");
    if (z.length !== 5) { setPlzHit(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/properties/lookup-plz", { params: { plz: z } });
        if (data?.found) {
          setPlzHit(data);
          // Stadt nur befüllen, wenn leer (User-Eingabe nicht überschreiben)
          if (!form.address?.city) setAddr("city", data.city);
        } else {
          setPlzHit(null);
        }
      } catch {
        setPlzHit(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [form.address?.zip]);

  const updateListItem = (key, idx, patch) => {
    setForm((s) => {
      const arr = [...(s[key] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...s, [key]: arr };
    });
  };
  const removeListItem = (key, idx) =>
    setForm((s) => ({ ...s, [key]: (s[key] || []).filter((_, i) => i !== idx) }));
  const addListItem = (key, factory) =>
    setForm((s) => ({ ...s, [key]: [...(s[key] || []), factory()] }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      // 2026-07 — Sekundäre Anschrift NUR mitschicken, wenn Checkbox aktiv.
      // Sonst hart auf null zurücksetzen, damit keine Alt-Daten hängenbleiben.
      has_secondary_address: !!form.has_secondary_address,
      secondary_address: form.has_secondary_address ? form.secondary_address : null,
      secondary_address_label: form.has_secondary_address ? (form.secondary_address_label || null) : null,
      build_year: num(form.build_year),
      renovation_year: num(form.renovation_year),
      land_area: num(form.land_area),
      living_area: num(form.living_area),
      commercial_area: num(form.commercial_area),
      unit_count: num(form.unit_count),
      floor_count: num(form.floor_count),
      energy_value: num(form.energy_value),
      energy_certificate_type: str(form.energy_certificate_type),
      energy_class: str(form.energy_class),
      energy_cert_valid_until: str(form.energy_cert_valid_until),
      heating_type: str(form.heating_type),
      heating_energy: str(form.heating_energy),
      hot_water_type: str(form.hot_water_type),
      owner_contact_id: form.owner_contact_id || null,
      management_start: form.management_start || null,
      caretaker_user_id: form.caretaker_user_id || null,
      // 2026-02 (iter120) — Verwaltungsart bereits bei Anlage
      object_type: form.object_type || "rental",
      weg_management: form.weg_management || (form.object_type === "seg" ? "external"
                        : form.object_type === "weg" || form.object_type === "mixed" ? "enabled"
                        : "none"),
      weg_total_shares: num(form.weg_total_shares) || 1000,
      meters: (form.meters || [])
        .filter((m) => (m.meter_number || "").trim() !== "")
        .map((m) => ({
          meter_number: m.meter_number,
          meter_type: m.meter_type,
          location: str(m.location),
          measurement_unit: str(m.measurement_unit),
        })),
      insurances: (form.insurances || [])
        .filter((i) => (i.company || "").trim() !== "")
        .map((i) => ({
          company: i.company,
          policy_number: str(i.policy_number),
          insurance_type: str(i.insurance_type),
          contract_start: str(i.contract_start),
          contract_end: str(i.contract_end),
          notes: str(i.notes),
        })),
    };
    try {
      const { data: created } = await api.post("/properties", payload);
      toast.success("Objekt angelegt · jetzt Einheiten hinzufügen");
      onSaved(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Neues Objekt</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        {/* === Grunddaten === */}
        <Section title="Grunddaten">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Objektname" className="col-span-2">
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="property-name" />
            </Field>
            <Field label="Objektart">
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROPERTY_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* 2026-02 (iter120) — Verwaltungsart bereits bei Anlage konfigurierbar.
              Wirkt später auf Buchhaltung, BKA, WEG-JA und die Einheitenanlage
              (bei WEG/Mixed wird „eigenständige ETW" vorausgewählt). */}
          <div className="mt-3 p-3 border border-amber-200 bg-amber-50/40 rounded-md">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-900 mb-2">
              Verwaltungsart · später jederzeit in den Stammdaten änderbar
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Objekt-Typ">
                <Select value={form.object_type} onValueChange={(v) => set("object_type", v)}>
                  <SelectTrigger data-testid="property-object-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rental">Miethaus (nur Vermietung)</SelectItem>
                    <SelectItem value="weg">WEG (Wohnungseigentümergemeinschaft)</SelectItem>
                    <SelectItem value="seg">SEG (Sondereigentumsverwaltung)</SelectItem>
                    <SelectItem value="mixed">Mixed (Miete + WEG)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {(form.object_type === "weg" || form.object_type === "mixed" || form.object_type === "seg") && (
                <Field label="WEG-Verwaltung">
                  <Select
                    value={form.weg_management || (form.object_type === "seg" ? "external" : "enabled")}
                    onValueChange={(v) => set("weg_management", v)}
                  >
                    <SelectTrigger data-testid="property-weg-management"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enabled">Wir verwalten die WEG</SelectItem>
                      <SelectItem value="external">Fremd-verwaltete WEG</SelectItem>
                      <SelectItem value="none">Keine WEG</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {(form.object_type === "weg" || form.object_type === "mixed" || form.object_type === "seg") && (
                <Field label="Gesamt-MEA (Nenner)">
                  <Input
                    type="number" min="1" step="1"
                    value={form.weg_total_shares}
                    onChange={(e) => set("weg_total_shares", e.target.value)}
                    placeholder="1000"
                    data-testid="property-weg-total-shares"
                  />
                </Field>
              )}
            </div>
            {form.object_type === "seg" && (form.weg_management || "external") === "external" && (
              <div className="text-[10px] text-amber-800 mt-1.5">
                Bei SEG mit Fremd-Verwaltung kannst du die zuständige externe Hausverwaltung
                unter <span className="font-mono">Kontakte → Fremd-verwaltete WEG</span> anlegen und später bei den Einheiten verknüpfen.
              </div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-3 mt-3">
            <Field label="Straße" className="col-span-2">
              <Input value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} data-testid="property-street" />
            </Field>
            <Field label="Nr."><Input value={form.address.house_number} onChange={(e) => setAddr("house_number", e.target.value)} /></Field>
            <Field label="PLZ">
              <Input
                value={form.address.zip}
                onChange={(e) => setAddr("zip", e.target.value)}
                placeholder="5-stellig"
                maxLength={5}
                data-testid="property-zip"
              />
            </Field>
            <Field label="Ort" className="col-span-3">
              <Input required value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="property-city" />
            </Field>
          </div>
          {plzHit && (
            <div className="-mt-1 text-[11px] text-slate-600 px-1" data-testid="plz-hint">
              <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-2 py-0.5">
                ✓ PLZ {plzHit.plz} · {plzHit.city} · Ortskennung: <span className="font-mono font-semibold">{plzHit.kfz}</span>
              </span>
              <span className="ml-2 text-slate-500">Die Objekt-ID wird automatisch als <span className="font-mono">{plzHit.kfz}-N</span> generiert.</span>
            </div>
          )}

          {/* 2026-07 — Zusätzliche Anschrift (Vorderhaus/Hinterhaus, Eckhäuser …) */}
          <div className="mt-3 border-t border-slate-100 pt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.has_secondary_address}
                onChange={(e) => set("has_secondary_address", e.target.checked)}
                data-testid="property-has-secondary-address"
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-slate-800">Zusätzliche Anschrift hinzufügen</div>
                <div className="text-[11px] text-slate-500">
                  Für Objekte mit 2 Postanschriften (z. B. Vorderhaus/Hinterhaus, Eckhäuser mit 2 Straßen).
                  Einzelne Einheiten können dann später der 2. Anschrift zugeordnet werden — bei Mietverhältnissen
                  wird automatisch die passende Anschrift verwendet.
                </div>
              </div>
            </label>
            {form.has_secondary_address && (
              <div className="mt-3 ml-6 space-y-2 border-l-2 border-emerald-200 pl-3" data-testid="property-secondary-address-block">
                <Field label="Bezeichnung der 2. Anschrift">
                  <Input
                    value={form.secondary_address_label}
                    onChange={(e) => set("secondary_address_label", e.target.value)}
                    placeholder='z. B. „Hinterhaus", „Eingang B", „Straße Süd"'
                    data-testid="property-secondary-label"
                  />
                </Field>
                <div className="grid grid-cols-4 gap-3">
                  <Field label="Straße" className="col-span-2">
                    <Input value={form.secondary_address.street} onChange={(e) => setSecAddr("street", e.target.value)} data-testid="property-secondary-street" />
                  </Field>
                  <Field label="Nr.">
                    <Input value={form.secondary_address.house_number} onChange={(e) => setSecAddr("house_number", e.target.value)} />
                  </Field>
                  <Field label="PLZ">
                    <Input value={form.secondary_address.zip} onChange={(e) => setSecAddr("zip", e.target.value)} maxLength={5} data-testid="property-secondary-zip" />
                  </Field>
                  <Field label="Ort" className="col-span-3">
                    <Input value={form.secondary_address.city} onChange={(e) => setSecAddr("city", e.target.value)} data-testid="property-secondary-city" />
                  </Field>
                </div>
              </div>
            )}
          </div>

          {form.object_type === "weg" ? (
            <Field label="Eigentümergemeinschaft (WEG)" className="mt-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableCombobox
                    value={form.owner_contact_id || ""}
                    onChange={(v) => set("owner_contact_id", v)}
                    options={(wegCommunities || []).map((w) => ({
                      id: w.id,
                      label: w.display_name || w.company_name || "(WEG ohne Name)",
                      sublabel: [w.address?.street && `${w.address.street} ${w.address.house_number || ""}`.trim(), w.address?.city].filter(Boolean).join(", ") || undefined,
                      keywords: [w.company_name, w.address?.street, w.address?.city, w.address?.zip, w.tax_id, ...(w.emails || [])].filter(Boolean).join(" "),
                    }))}
                    placeholder="WEG suchen…"
                    allowClear
                    testId="property-weg-select"
                  />
                </div>
                <Dialog open={wegDialogOpen} onOpenChange={setWegDialogOpen}>
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" className="gap-1.5" data-testid="new-weg-btn">
                      <UserPlus size={14} /> Neue WEG
                    </Button>
                  </DialogTrigger>
                  <WegCommunityDialog
                    prefillFromProperty={form}
                    onSaved={async (newWeg) => {
                      setWegDialogOpen(false);
                      await reloadWegCommunities();
                      set("owner_contact_id", newWeg.id);
                    }}
                  />
                </Dialog>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Bei einer WEG wird das Objekt der Eigentümergemeinschaft zugeordnet.
                Die einzelnen Einheiten erhalten später ihre eigenen Sondereigentümer.
              </div>
            </Field>
          ) : (
          <Field label="Eigentümer" className="mt-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchableCombobox
                  value={form.owner_contact_id || ""}
                  onChange={(v) => set("owner_contact_id", v)}
                  options={owners.map((o) => ({
                    id: o.id,
                    label: `${o.owner_number ? `[${o.owner_number}] ` : ""}${o.display_name || `${o.first_name || ""} ${o.last_name || ""}`.trim() || o.company_name || "(unbenannt)"}`,
                    sublabel: [o.emails?.[0], o.phones?.[0]].filter(Boolean).join(" · ") || undefined,
                    keywords: [o.owner_number, o.first_name, o.last_name, o.company_name, ...(o.emails || []), ...(o.phones || [])].filter(Boolean).join(" "),
                  }))}
                  placeholder="Eigentümer suchen…"
                  allowClear
                  testId="property-owner-select"
                />
              </div>
              <Dialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" className="gap-1.5" data-testid="new-owner-btn">
                    <UserPlus size={14} /> Neu
                  </Button>
                </DialogTrigger>
                <OwnerDialog
                  onSaved={async (newOwner) => {
                    setOwnerDialogOpen(false);
                    await reloadOwners();
                    set("owner_contact_id", newOwner.id);
                  }}
                />
              </Dialog>
            </div>
          </Field>
          )}

          <Field label="Zuständiger Betreuer" className="mt-3">
            <Select value={form.caretaker_user_id || "none"} onValueChange={(v) => set("caretaker_user_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="property-caretaker-select"><SelectValue placeholder="Betreuer wählen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Kein Betreuer —</SelectItem>
                {staff.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400">· {u.role}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {form.object_type !== "weg" && (
            <Field label={'Verwaltungsbeginn (optional, wenn später als „Eigentum seit")'} className="mt-3">
              <Input
                type="date"
                value={form.management_start || ""}
                onChange={(e) => set("management_start", e.target.value)}
                data-testid="property-management-start"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Wenn Sie das Objekt erst später zur Verwaltung übernommen haben (z. B.
                Portfolio-Übernahme vom Vor-Verwalter), können Sie hier ein späteres
                Datum setzen. Sollstellungen werden dann erst ab diesem Datum geführt.
                „Eigentum seit" wird nach dem Anlegen im Objekt-Detail gepflegt.
              </div>
            </Field>
          )}
          {form.object_type === "weg" && (
            <div className="mt-3 border border-amber-200 bg-amber-50/50 rounded-md p-2.5 text-[11px] text-amber-900">
              <b>WEG:</b> „Eigentum seit" und „Verwaltungsbeginn" werden pro Sondereigentums-Einheit
              gepflegt. Nach dem Anlegen des WEG-Objekts können Sie die Daten je Einheit setzen
              (Einheit öffnen → Sektion <i>„Eigentum & Verwaltung"</i>).
            </div>
          )}
        </Section>

        {/* === Gebäude === */}
        <Section title="Gebäude & Flächen">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Baujahr"><Input type="number" value={form.build_year} onChange={(e) => set("build_year", e.target.value)} /></Field>
            <Field label="Sanierungsjahr"><Input type="number" value={form.renovation_year} onChange={(e) => set("renovation_year", e.target.value)} /></Field>
            <Field label="Geschosse"><Input type="number" value={form.floor_count} onChange={(e) => set("floor_count", e.target.value)} /></Field>
            <Field label="Einheiten"><Input type="number" value={form.unit_count} onChange={(e) => set("unit_count", e.target.value)} /></Field>
            <Field label="Grundstück (m²)"><Input type="number" value={form.land_area} onChange={(e) => set("land_area", e.target.value)} /></Field>
            <Field label="Wohnfl. (m²)"><Input type="number" value={form.living_area} onChange={(e) => set("living_area", e.target.value)} /></Field>
            <Field label="Gewerbefl. (m²)"><Input type="number" value={form.commercial_area} onChange={(e) => set("commercial_area", e.target.value)} /></Field>
          </div>
          <div className="flex gap-5 mt-3 text-sm">
            <Toggle label="Aufzug" checked={form.has_elevator} onChange={(v) => set("has_elevator", v)} />
            <Toggle label="Keller" checked={form.has_basement} onChange={(v) => set("has_basement", v)} />
            <Toggle label="Dachboden" checked={form.has_attic} onChange={(v) => set("has_attic", v)} />
          </div>
        </Section>

        {/* === Heizung & Energie === */}
        <Section title="Heizung & Energie">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Heizungsart">
              <Select value={form.heating_type || "none"} onValueChange={(v) => set("heating_type", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="central">Zentralheizung</SelectItem>
                  <SelectItem value="floor">Etagenheizung</SelectItem>
                  <SelectItem value="district">Fernwärme</SelectItem>
                  <SelectItem value="heat_pump">Wärmepumpe</SelectItem>
                  <SelectItem value="single_stove">Einzelofen</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Energieträger">
              <Select value={form.heating_energy || "none"} onValueChange={(v) => set("heating_energy", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="gas">Gas</SelectItem>
                  <SelectItem value="oil">Öl</SelectItem>
                  <SelectItem value="electric">Strom</SelectItem>
                  <SelectItem value="pellets">Pellets</SelectItem>
                  <SelectItem value="district">Fernwärme</SelectItem>
                  <SelectItem value="solar">Solar</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Warmwasser">
              <Select value={form.hot_water_type || "none"} onValueChange={(v) => set("hot_water_type", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="central">Zentral</SelectItem>
                  <SelectItem value="decentral">Dezentral</SelectItem>
                  <SelectItem value="flow_heater">Durchlauferhitzer</SelectItem>
                  <SelectItem value="boiler">Boiler</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            <Field label="Energieausweis">
              <Select value={form.energy_certificate_type || "none"} onValueChange={(v) => set("energy_certificate_type", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="consumption">Verbrauch</SelectItem>
                  <SelectItem value="demand">Bedarf</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="kWh/(m²·a)"><Input type="number" step="0.1" value={form.energy_value} onChange={(e) => set("energy_value", e.target.value)} /></Field>
            <Field label="Klasse"><Input value={form.energy_class} onChange={(e) => set("energy_class", e.target.value)} placeholder="A, B, …" /></Field>
            <Field label="Gültig bis"><Input type="date" value={form.energy_cert_valid_until} onChange={(e) => set("energy_cert_valid_until", e.target.value)} /></Field>
          </div>

          {/* Zähler */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-700">Zähler</div>
              <Button type="button" variant="outline" size="sm" className="h-7 gap-1"
                onClick={() => addListItem("meters", newMeter)} data-testid="add-meter-btn">
                <Plus size={12} /> Zähler hinzufügen
              </Button>
            </div>
            {(form.meters || []).length === 0 && (
              <div className="text-xs text-slate-400 italic">Noch keine Zähler erfasst.</div>
            )}
            <div className="space-y-2">
              {(form.meters || []).map((m, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white border border-slate-200 rounded-sm p-2"
                  data-testid={`meter-row-${idx}`}>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-slate-600">Zählerart</Label>
                    <Select value={m.meter_type} onValueChange={(v) => updateListItem("meters", idx, { meter_type: v, measurement_unit: METER_UNIT_SUGGESTION[v] || m.measurement_unit })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(METER_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-slate-600">Zählernummer</Label>
                    <Input className="h-8" value={m.meter_number}
                      onChange={(e) => updateListItem("meters", idx, { meter_number: e.target.value })}
                      data-testid={`meter-number-${idx}`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-slate-600">Standort</Label>
                    <Input className="h-8" value={m.location || ""}
                      placeholder="z. B. Keller"
                      onChange={(e) => updateListItem("meters", idx, { location: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-slate-600">Einheit</Label>
                    <Input className="h-8" value={m.measurement_unit || ""}
                      placeholder="kWh / m³"
                      onChange={(e) => updateListItem("meters", idx, { measurement_unit: e.target.value })} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-600"
                      onClick={() => removeListItem("meters", idx)} data-testid={`meter-remove-${idx}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* === Versicherungen (mehrfach) === */}
        <Section title="Versicherungen">
          <div className="flex items-center justify-end mb-2">
            <Button type="button" variant="outline" size="sm" className="h-7 gap-1"
              onClick={() => addListItem("insurances", newInsurance)} data-testid="add-insurance-btn">
              <Plus size={12} /> Versicherung hinzufügen
            </Button>
          </div>
          {(form.insurances || []).length === 0 && (
            <div className="text-xs text-slate-400 italic">Noch keine Versicherung erfasst.</div>
          )}
          <div className="space-y-2">
            {(form.insurances || []).map((ins, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white border border-slate-200 rounded-sm p-2"
                data-testid={`insurance-row-${idx}`}>
                <div className="col-span-3">
                  <Label className="text-[10px] text-slate-600">Art</Label>
                  <Select value={ins.insurance_type || "other"} onValueChange={(v) => updateListItem("insurances", idx, { insurance_type: v })}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(INSURANCE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] text-slate-600">Versicherer</Label>
                  <Input className="h-8" value={ins.company}
                    onChange={(e) => updateListItem("insurances", idx, { company: e.target.value })}
                    data-testid={`insurance-company-${idx}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-slate-600">Police-Nr.</Label>
                  <Input className="h-8" value={ins.policy_number || ""}
                    onChange={(e) => updateListItem("insurances", idx, { policy_number: e.target.value })} />
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] text-slate-600">Beginn</Label>
                  <Input className="h-8" type="date" value={ins.contract_start || ""}
                    onChange={(e) => updateListItem("insurances", idx, { contract_start: e.target.value })} />
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] text-slate-600">Ende</Label>
                  <Input className="h-8" type="date" value={ins.contract_end || ""}
                    onChange={(e) => updateListItem("insurances", idx, { contract_end: e.target.value })} />
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-600"
                    onClick={() => removeListItem("insurances", idx)} data-testid={`insurance-remove-${idx}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Section>


        <Field label="Notizen">
          <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="property-save-btn">Anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ============================================================
// Owner quick-create dialog (full)
// ============================================================
function OwnerDialog({ onSaved }) {
  const [form, setForm] = useState({
    contact_type: "person",
    salutation: "",
    first_name: "", last_name: "", company_name: "", contact_person: "",
    birth_date: "",
    address: { street: "", house_number: "", zip: "", city: "", country: "DE" },
    email: "", phone: "",
    iban: "", bic: "", tax_id: "",
    notes: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setAddr = (k, v) => setForm((s) => ({ ...s, address: { ...s.address, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      contact_type: form.contact_type,
      salutation: form.contact_type === "person" ? (form.salutation || null) : null,
      first_name: form.contact_type === "person" ? (form.first_name || null) : null,
      last_name: form.contact_type === "person" ? (form.last_name || null) : null,
      company_name: form.contact_type !== "person" ? (form.company_name || null) : null,
      contact_person: form.contact_person || null,
      birth_date: form.birth_date || null,
      address: form.address,
      emails: form.email ? [form.email] : [],
      phones: form.phone ? [form.phone] : [],
      iban: form.iban || null,
      bic: form.bic || null,
      tax_id: form.tax_id || null,
      notes: form.notes || null,
      roles: ["owner"],
    };
    try {
      const { data } = await api.post("/contacts", payload);
      toast.success(`Eigentümer angelegt${data.owner_number ? ` (#${data.owner_number})` : ""}`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Neuer Eigentümer</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <Section title="Stammdaten">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Typ">
              <Select value={form.contact_type} onValueChange={(v) => set("contact_type", v)}>
                <SelectTrigger data-testid="owner-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">Person</SelectItem>
                  <SelectItem value="company">Firma</SelectItem>
                  <SelectItem value="institution">Institution</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Geburtsdatum (optional)">
              <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} />
            </Field>
          </div>

          {form.contact_type === "person" ? (
            <div className="grid grid-cols-3 gap-3 mt-3">
              <Field label="Anrede">
                <Select value={form.salutation || ""} onValueChange={(v) => set("salutation", v)}>
                  <SelectTrigger data-testid="owner-salutation"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SALUTATIONS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Vorname"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="owner-first-name" /></Field>
              <Field label="Nachname"><Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="owner-last-name" /></Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Firmenname" className="col-span-1"><Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="owner-company-name" /></Field>
              <Field label="Ansprechpartner"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
            </div>
          )}
        </Section>

        <Section title="Adresse">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Straße" className="col-span-2"><Input value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} /></Field>
            <Field label="Nr."><Input value={form.address.house_number} onChange={(e) => setAddr("house_number", e.target.value)} /></Field>
            <Field label="PLZ"><Input value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} /></Field>
            <Field label="Ort" className="col-span-3"><Input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} /></Field>
            <Field label="Land"><Input value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Kontakt">
          <div className="grid grid-cols-2 gap-3">
            <Field label="E-Mail"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="owner-email" /></Field>
            <Field label="Telefon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Bank & Steuer">
          <div className="grid grid-cols-3 gap-3">
            <Field label="IBAN" className="col-span-2"><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} placeholder="DE.." /></Field>
            <Field label="BIC"><Input value={form.bic} onChange={(e) => set("bic", e.target.value)} /></Field>
            <Field label="Steuer-ID" className="col-span-3"><Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} /></Field>
          </div>
        </Section>

        <Field label="Notizen">
          <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="owner-save-btn">Eigentümer anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <input type="checkbox" className="h-4 w-4 accent-slate-900" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-slate-700">{label}</span>
    </label>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-700 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


// ============================================================
// WEG-Eigentümergemeinschaft anlegen
// ============================================================
function WegCommunityDialog({ prefillFromProperty, onSaved }) {
  const [form, setForm] = useState(() => {
    const p = prefillFromProperty || {};
    const streetPart = [p.address?.street, p.address?.house_number].filter(Boolean).join(" ");
    const defaultName = streetPart
      ? `WEG ${streetPart}${p.address?.city ? `, ${p.address.city}` : ""}`
      : (p.name ? `WEG ${p.name}` : "");
    return {
      company_name: defaultName,
      address: { ...(p.address || { street: "", house_number: "", zip: "", city: "", country: "DE" }) },
      tax_id: "",
      iban: "",
      bic: "",
      contact_person: "",
      email: "",
      phone: "",
      notes: "",
    };
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setAddr = (k, v) => setForm((s) => ({ ...s, address: { ...s.address, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      contact_type: "weg_community",
      company_name: form.company_name || null,
      contact_person: form.contact_person || null,
      address: form.address,
      emails: form.email ? [form.email] : [],
      phones: form.phone ? [form.phone] : [],
      iban: form.iban || null,
      bic: form.bic || null,
      tax_id: form.tax_id || null,
      notes: form.notes || null,
      roles: [],
    };
    try {
      const { data } = await api.post("/contacts", payload);
      toast.success("Eigentümergemeinschaft angelegt");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Anlegen");
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="weg-community-dialog">
      <DialogHeader>
        <DialogTitle>Neue Eigentümergemeinschaft</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <Section title="Stammdaten">
          <div className="grid grid-cols-1 gap-3">
            <Field label="Name der WEG">
              <Input
                required
                value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                placeholder="z. B. WEG Linienstraße 42"
                data-testid="weg-name"
              />
            </Field>
            <Field label="Vertretung / Beirat (optional)">
              <Input
                value={form.contact_person}
                onChange={(e) => set("contact_person", e.target.value)}
                placeholder="z. B. Max Mustermann (Verwaltungsbeirat)"
                data-testid="weg-representative"
              />
            </Field>
          </div>
        </Section>

        <Section title="Adresse der WEG">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Straße" className="col-span-2">
              <Input value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} data-testid="weg-street" />
            </Field>
            <Field label="Nr.">
              <Input value={form.address.house_number} onChange={(e) => setAddr("house_number", e.target.value)} />
            </Field>
            <Field label="PLZ">
              <Input value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} data-testid="weg-zip" />
            </Field>
            <Field label="Ort" className="col-span-3">
              <Input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="weg-city" />
            </Field>
            <Field label="Land">
              <Input value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Bankverbindung & Steuer (optional)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="IBAN">
              <Input value={form.iban} onChange={(e) => set("iban", e.target.value)} placeholder="DE…" />
            </Field>
            <Field label="BIC">
              <Input value={form.bic} onChange={(e) => set("bic", e.target.value)} />
            </Field>
            <Field label="Steuer-Nr. / USt-IdNr.">
              <Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Kontakt (optional)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="E-Mail">
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Telefon">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
          </div>
          <Field label="Notizen" className="mt-3">
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </Section>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="weg-save-btn">
            Eigentümergemeinschaft anlegen
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
